// Initialize Sentry before anything else so early errors are captured. Next
// auto-loaded this from the project root; the SPA imports it explicitly.
import "../sentry.client.config"
import "@/i18n"
import "@/styles/globals.css"
import "@/styles/globals.scss"
import "@/styles/lib/nx-icons-font.scss"
import "@/styles/lib/slussen-mono-font.scss"
import "@agent-nexus/ui/styles/tokens.css"

import { NxTooltipProvider } from "@agent-nexus/ui"
import { lazy, Suspense, useEffect } from "react"
import { createRoot } from "react-dom/client"
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from "react-router"

import { AppComponents, AppHead, AppProviders, isEmbedPage } from "@/common/app"
import { RouteErrorBoundary } from "@/common/components/Error"
import { MaintenanceBanner } from "@/common/components/MaintenanceBanner"
import SplashScreen from "@/common/components/Splashscreen"
import { ToastProviderComplete } from "@/common/components/UI/Toast"
import { useDevDarkModeToggle } from "@/common/hooks/useDevDarkModeToggle"
import { useFaviconSwitcher } from "@/common/hooks/useFaviconSwitcher"
import { resolveLegacyRedirect } from "@/common/routing/legacy-redirects"
import { attemptStaleChunkReload } from "@/common/utils/stale-chunk.util"
import { useRouteChangeEmitter } from "@/common/vite-shims/next/router"
import { GeneratingAgentsProvider } from "@/modules/MyAgents/contexts/GeneratingAgentsContext"
import { pageRoutes } from "@/routes"

/**
 * The console-only half of the shell, behind a real code-split boundary.
 *
 * 🚨 A CONDITIONAL RENDER IS NOT A CONDITIONAL DOWNLOAD. Both of these are
 * rendered under `!isEmbed`, and both were STATICALLY imported — so the public
 * `/embed/:embed-id` route, which mounts neither, still downloaded them.
 *
 * They are also the only two eager doors into `common/api/useNexusApi`, which
 * imports all 106 generated domain clients in one module:
 *
 *   main.tsx -> common/app -> auth/UserIdentification -> api/useNexusApi
 *   main.tsx -> common/boot/boot -> auth/useAuthRedirect -> hooks/useFeatureFlag       -> api/useNexusApi
 *   main.tsx -> common/boot/boot -> auth/useAuthRedirect -> hooks/permissions/useFeatureConfinement -> api/useNexusApi
 *   main.tsx -> common/boot/boot -> hooks/useUiPreferences                             -> api/useNexusApi
 *
 * FOUR doors, one chunk: leaving any one of them static keeps the whole
 * `api-client` chunk a static dependency of the entry, so this has to be all of
 * them or none. `UserIdentification` was also removed from the `common/app`
 * barrel — `routes.tsx` imports that barrel eagerly, so a barrel re-export is a
 * fifth door that no edit to this file can close.
 *
 * The console is unchanged: `UserIdentification` renders `<></>` and only runs
 * effects, and `Boot`'s own first paint is `<SplashScreen turnOff={false}/>`,
 * which is exactly the fallback below.
 */
const Boot = lazy(() => import("@/common/boot/boot"))
const UserIdentification = lazy(() =>
  import("@/common/auth/UserIdentification").then((m) => ({ default: m.UserIdentification }))
)

/**
 * SPA entry (NEX-248 Phase C). Reproduces the former `_app.tsx` provider tree
 * as a React Router layout route: the shell persists across navigations while
 * the matched page renders into <Outlet/>. Embed pages render bare (no Clerk,
 * no Boot, no Intercom), exactly as `_app` branched on `isEmbedPage`.
 */
function AppShell() {
  const { pathname, search, hash } = useLocation()
  const isEmbed = isEmbedPage(pathname)

  // Drives next/router routeChangeStart events off real location changes.
  useRouteChangeEmitter()
  useDevDarkModeToggle()
  useFaviconSwitcher(pathname)

  // Admin panel background/scrollbar class, managed at router level so it
  // persists across admin navigations without flashing per page.
  useEffect(() => {
    const html = document.documentElement
    if (pathname.startsWith("/admin")) {
      html.classList.add("admin")
    } else {
      html.classList.remove("admin")
    }
  }, [pathname])

  // Legacy URL compatibility (formerly next.config rewrites/redirects). Resolved
  // here, above the page <Outlet/>, so an old URL never renders a 404 first. The
  // incoming query + hash are carried onto the destination (Next kept the full URL).
  const legacyTarget = resolveLegacyRedirect(pathname, search, hash)
  if (legacyTarget) return <Navigate to={legacyTarget} replace />

  return (
    <>
      <AppHead />
      {!isEmbed && <MaintenanceBanner />}
      <AppProviders pageProps={{}} isEmbed={isEmbed}>
        <NxTooltipProvider>
          <ToastProviderComplete>
            <GeneratingAgentsProvider>
              {!isEmbed && (
                <Suspense fallback={null}>
                  <UserIdentification />
                </Suspense>
              )}
              {isEmbed ? (
                <Outlet />
              ) : (
                <Suspense fallback={<SplashScreen turnOff={false} />}>
                  <Boot>
                    <Outlet />
                  </Boot>
                </Suspense>
              )}
              {!isEmbed && <AppComponents.IntercomComponent />}
            </GeneratingAgentsProvider>
          </ToastProviderComplete>
        </NxTooltipProvider>
      </AppProviders>
    </>
  )
}

/**
 * Vite fires this when a dynamically imported chunk fails to load — after a
 * deploy, that means this document is stale and is asking for assets the server
 * no longer has. Starting the reload here gets it moving as early as possible,
 * before React has to render anything.
 *
 * Deliberately does NOT call `event.preventDefault()`. Vite's preload helper is
 * `baseModule().catch(handlePreloadError)`, and `handlePreloadError` only
 * rethrows `if (!e.defaultPrevented)` — so suppressing the event makes the whole
 * import RESOLVE with `undefined` instead of rejecting. The lazy factory then
 * destructures undefined and throws "Cannot read properties of undefined", which
 * is not recognisable as a stale chunk: the user would get the generic error
 * state during a successful recovery, and Sentry would receive a bogus report
 * polluting the very signal this change exists to restore.
 *
 * Letting it throw keeps the original error intact, so `RouteErrorBoundary`
 * classifies it correctly and shows the reloading state while this reload lands.
 */
window.addEventListener("vite:preloadError", () => {
  attemptStaleChunkReload()
})

const router = createBrowserRouter([
  { element: <AppShell />, errorElement: <RouteErrorBoundary />, children: pageRoutes }
])

const container = document.getElementById("__next")
if (!container) throw new Error("Root element #__next not found")
createRoot(container).render(<RouterProvider router={router} />)
